import { Card, Flex, Table, Text, TitledText, theme } from '@/components';
import { usePolicies } from '@/features/master-table/policy/_hooks/policy';
import type { PoliciesForProjectModel } from '@/features/project/projects/_models/project';
import { findUpperRecursive } from '@/utils';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Props = {
  policyForProject: z.infer<typeof PoliciesForProjectModel>;
  hidePolicyUrl?: boolean;
  isMigrated?: boolean;
};

export const PolicyForProjectCard = ({
  policyForProject,
  hidePolicyUrl,
  isMigrated,
}: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const policies = usePolicies({ params: { isMigrated } });
  const currentPolicies = findUpperRecursive(
    policyForProject.policyId,
    policies.data ?? [],
  ).reverse();

  const PolicyLabelModifier = [
    t('policy-level-1'),
    t('policy-level-2'),
    t('policy-level-3'),
  ];

  return (
    <Card>
      <Flex gap={16} vertical>
        <Text variant="body" color="secondary" text={t('policy')} />

        <Table
          enableSticky={false}
          columns={[
            {
              dataIndex: 'label',
              rowScope: 'row',
              render: (text) => <Text color="secondary" text={text} />,
            },
            { dataIndex: 'value' },
          ]}
          dataSource={currentPolicies.map((currentPolicy, index) => ({
            key: `${currentPolicy.id}-${index}`,
            label: PolicyLabelModifier[index],
            value: currentPolicy.name,
          }))}
          pagination={false}
          showHeader={false}
          style={{ borderTop: `1px solid ${token.colorBorder}` }}
        />

        {hidePolicyUrl ? null : (
          <TitledText
            variant="link"
            title={t('policy-url')}
            text={policyForProject.policyUrl}
            href={policyForProject.policyUrl ?? ''}
          />
        )}
      </Flex>
    </Card>
  );
};
